import { ConfirmationRequest, Message, ToolExecution } from "../stores/useCopilotStore";
import { PageContext } from "../hooks/usePageContext";

export interface WebSocketCallbacks {
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
  onMessage?: (data: any) => void;
  onAuthSuccess?: (data: any) => void;
  onAuthError?: (message: string) => void;
  onAssistantMessage?: (message: Message) => void;
  onConfirmationRequest?: (request: ConfirmationRequest) => void;
  onHistory?: (messages: Message[]) => void;
  onToolExecutionStarted?: (execution: ToolExecution) => void;
  onToolExecutionFailed?: (execution: ToolExecution) => void;
  onToolExecutionCompleted?: (execution: ToolExecution) => void;
  onInvalidationRequest?: (entities: string[]) => void;
  onProcessingError?: (error: string) => void;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private callbacks: WebSocketCallbacks = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelays = [1000, 2000, 4000, 8000, 16000, 30000];
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private sessionId: string | null = null;
  private isConnecting = false;
  private shouldReconnect = true;

  constructor() {
    console.log('[WebSocketService] Service initialized');
  }

  async connect(
    sessionId: string,
    accessToken: string,
    callbacks: WebSocketCallbacks
  ): Promise<void> {
    console.log('[WebSocketService] Connect called with session:', sessionId);

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WebSocketService] Already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('[WebSocketService] Already connecting');
      return;
    }

    this.isConnecting = true;
    this.sessionId = sessionId;
    this.callbacks = callbacks;
    this.shouldReconnect = true;

    try {
      // First, ensure we can reach the backend
      await this.checkBackendHealth();

      const wsUrl = process.env.NEXT_PUBLIC_AI_BACKEND_WS_URL || 'ws://localhost:8000';
      const fullUrl = `${wsUrl}/api/v1/ws/chat`;

      console.log('[WebSocketService] Creating WebSocket to:', fullUrl);

      // Create the WebSocket
      this.ws = new WebSocket(fullUrl);

      // Set up event handlers
      this.setupEventHandlers(accessToken);

    } catch (error) {
      console.error('[WebSocketService] Connection failed:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(new Event('Connection failed'));
      throw error;
    }
  }

  private async checkBackendHealth(): Promise<void> {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_AI_BACKEND_URL || 'http://localhost:8000'}/api/v1/health/`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Backend health check failed: ${response.status}`);
      }

      console.log('[WebSocketService] Backend health check passed');
    } catch (error) {
      console.error('[WebSocketService] Backend not accessible:', error);
      throw new Error('AI backend is not accessible. Please ensure the server is running.');
    }
  }

  private setupEventHandlers(accessToken: string): void {
    console.log('[WebSocketService] Setting up event handlers');
    if (!this.ws) {
      console.error('[WebSocketService] No WebSocket instance to setup handlers for');
      return;
    }

    console.log('[WebSocketService] WebSocket instance found, setting up handlers');

    this.ws.onopen = () => {
      console.log('[WebSocketService] WebSocket opened');
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      // Send authentication immediately
      const authMessage = {
        type: 'auth',
        session_id: this.sessionId,
        access_token: accessToken
      };

      console.log('[WebSocketService] Sending auth for session:', this.sessionId);
      this.ws!.send(JSON.stringify(authMessage));

      this.callbacks.onOpen?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[WebSocketService] Message received:', data.type);

        this.handleMessage(data);
      } catch (error) {
        console.error('[WebSocketService] Failed to parse message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocketService] WebSocket error:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error);
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocketService] WebSocket closed:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      this.isConnecting = false;
      this.ws = null;

      this.callbacks.onClose?.(event);

      // Attempt reconnection if needed
      if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect(accessToken);
      }
    };
  }

  private handleMessage(data: any): void {
    switch (data.type) {
      case 'auth_success':
        console.log('[WebSocketService] Authentication successful');
        this.callbacks.onAuthSuccess?.(data);
        break;

      case 'error':
        console.error('[WebSocketService] Error message:', data.message);
        if (data.message.includes('auth') || data.message.includes('token')) {
          this.callbacks.onAuthError?.(data.message);
        } else if (data.message.includes('Failed to process message') ||
          data.message.includes('Failed to parse tool call arguments')) {
          // This is a processing error, not an auth error
          this.callbacks.onProcessingError?.(data.message);
        }
        this.callbacks.onMessage?.(data);
        break;

      case 'history':
        console.log('[WebSocketService] Received history');
        const historyMessages: Message[] = data.messages.map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.created_at),
          tools_used: msg.tools_used,
          processing_time_ms: msg.processing_time_ms,
        }));
        this.callbacks.onHistory?.(historyMessages);
        break;

      case 'message':
        console.log('[WebSocketService] Received assistant message');
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: data.content,
          timestamp: new Date(),
          tools_used: data.tools_used,
          processing_time_ms: data.processing_time_ms,
        };
        this.callbacks.onAssistantMessage?.(assistantMessage);
        break;

      case 'confirmation_request':
        console.log('[WebSocketService] Received confirmation request');
        const confirmationRequest: ConfirmationRequest = {
          id: data.id,
          tool: data.tool,
          action: data.action,
          details: data.details,
          timestamp: new Date(),
          tool_call_id: data.tool_call_id, // Include tool_call_id
        };
        this.callbacks.onConfirmationRequest?.(confirmationRequest);
        break;

      case 'tool_execution_started':
        console.log('[WebSocketService] Tool execution started:', data.tool_name);
        const startedExecution: ToolExecution = {
          tool_call_id: data.tool_call_id,
          tool_name: data.tool_name,
          status: 'started',
          timestamp: new Date(),
          requires_confirmation: data.requires_confirmation,
        };
        this.callbacks.onToolExecutionStarted?.(startedExecution);
        break;

      case 'tool_execution_failed':
        console.log('[WebSocketService] Tool execution failed:', data.tool_name);
        const failedExecution: ToolExecution = {
          tool_call_id: data.tool_call_id,
          tool_name: data.tool_name,
          status: 'failed',
          failure_type: data.failure_type,
          reason: data.reason,
          timestamp: new Date(),
          requires_confirmation: false, // Will be updated from store
        };
        this.callbacks.onToolExecutionFailed?.(failedExecution);
        break;

      case 'tool_execution_completed':
        console.log('[WebSocketService] Tool execution completed:', data.tool_name);
        const completedExecution: ToolExecution = {
          tool_call_id: data.tool_call_id,
          tool_name: data.tool_name,
          status: 'completed',
          result: data.result,
          timestamp: new Date(),
          requires_confirmation: false, // Will be updated from store
        };
        this.callbacks.onToolExecutionCompleted?.(completedExecution);
        break;

      case 'invalidation_request':
        console.log('[WebSocketService] Received invalidation request for entities:', data.entities);
        this.callbacks.onInvalidationRequest?.(data.entities);
        break;

      case 'token_refresh_request':
        console.log('[WebSocketService] Received token refresh request:', data.reason);
        this.handleTokenRefreshRequest(data.reason);
        break;

      default:
        console.log('[WebSocketService] Unknown message type:', data.type);
        this.callbacks.onMessage?.(data);
    }
  }

  private async handleTokenRefreshRequest(reason: string): Promise<void> {
    console.log('[WebSocketService] Handling token refresh request:', reason);

    try {
      // Import createClient to get fresh token
      const { createClient } = await import('@/app/utils/supabase/client');
      const supabase = createClient();

      // Get current session with fresh token
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session) {
        console.error('[WebSocketService] Failed to get session for token refresh:', error);
        return;
      }

      // Send refreshed token back to backend
      this.sendTokenRefresh(session.access_token);
    } catch (error) {
      console.error('[WebSocketService] Error refreshing token:', error);
    }
  }

  private scheduleReconnect(accessToken: string): void {
    const delay = this.reconnectDelays[Math.min(this.reconnectAttempts, this.reconnectDelays.length - 1)];

    console.log(`[WebSocketService] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectAttempts++;

      // Try to get a fresh token for reconnection
      try {
        const { createClient } = await import('@/app/utils/supabase/client');
        const supabase = createClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          accessToken = session.access_token;
          console.log('[WebSocketService] Using fresh token for reconnection');
        }
      } catch (error) {
        console.log('[WebSocketService] Could not refresh token for reconnection, using existing');
      }

      this.connect(this.sessionId!, accessToken, this.callbacks);
    }, delay);
  }

  sendMessage(content: string, context?: PageContext): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocketService] Cannot send message - WebSocket not connected');
      return;
    }

    const message = {
      type: 'message',
      content,
      context,
    };

    console.log('[WebSocketService] Sending message with context:', context?.pageType);
    this.ws.send(JSON.stringify(message));
  }

  sendConfirmationResponse(confirmationId: string, approved: boolean, reason?: string, data?: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocketService] Cannot send confirmation - WebSocket not connected');
      return;
    }

    const response = {
      type: 'confirmation_response',
      confirmation_id: confirmationId,
      approved,
      reason,
      ...(data && { data }),
    };

    console.log('[WebSocketService] Sending confirmation response');
    this.ws.send(JSON.stringify(response));
  }

  sendTokenRefresh(accessToken: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocketService] Cannot send token refresh - WebSocket not connected');
      return;
    }

    const response = {
      type: 'token_refresh_response',
      access_token: accessToken
    };

    console.log('[WebSocketService] Sending proactive token refresh');
    this.ws.send(JSON.stringify(response));
  }

  disconnect(): void {
    console.log('[WebSocketService] Disconnecting');

    this.shouldReconnect = false;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();